import React from 'react'
import { getClassNames } from '../../../_utils/classnames'
import { IJourneyWizard } from '../interfaces'
import { ConfigurationEditor } from '../../journey-builder/configuration-editor/configuration-editor'
import { JourneyBuilderMode } from '../../journey-builder/enums'
import {
    getEditableState,
    getJourneyStatusOnPublish,
    getPublishedJourneyStatusChange,
    isJourneyUnpublished,
    JourneyEditableState,
} from '../../journey-builder/helpers/journey'
import { dateNow } from '../../../_utils/datetime'
import { simpleNotification } from '../../../_utils/utils'
import deepEqual from 'react-fast-compare'
import { extractJourneyTrigger } from '../../../_utils/journeys'
import { IConfigurationFlags } from '../../journey-builder/configuration-editor/interfaces'
import { generateShortID, generateUID } from '../../../_utils/uid'
import {
    JOURNEY_ABANDONED_BROWSE_MAXIMUM_ITEM_VIEWS,
    JOURNEY_ABANDONED_BROWSE_MINIMUM_ITEM_VIEWS,
} from '../../journey-builder/configuration-editor/constants'
import { getEnabledDeliveryChannels } from '../../../_utils/domain'
import { DomainDto } from '../../../dtos/domain'
import { TriggerType } from '@pushly/models/lib/enums'
import { ExitStatus, JourneyType, StepType } from '@pushly/models/lib/enums/journeys'
import { ExitType } from '@pushly/models/lib/enums/journeys/exit-type.enum'
import type { Journey } from '../../../features/journeys/types/journey-api-response'
import { serializeJourney } from '../../../features/journeys/utils/journey-serialization'
import {
    applyJourneyAbandonmentDefaults,
    applyJourneyDefaultAudienceSegment,
} from '../../../features/journeys/utils/journey-configuration-defaults'
import { SegmentDto } from '../../../dtos/segment'
import { confirmJourneyStatusChange } from '../../../features/journeys/components/journey-status-change-confirmation'

interface IJourneyConfigurationTab extends IJourneyWizard {
    domain: DomainDto
    disableRouterPrompt?: boolean
}

interface IState {
    tmpValue?: Journey
    domainDefaultSegmentId?: number
}

export class JourneyConfigurationTab extends React.Component<IJourneyConfigurationTab, IState> {
    public static tabName = 'configuration'
    public static tabLabel = 'Configuration'

    private unmounting: boolean = false

    public constructor(props: IJourneyConfigurationTab) {
        super(props)

        this.state = {
            tmpValue: this.props.journey ?? (this.getInitialJourneyBuild(this.props.domain) as Journey),
        }
    }

    public componentDidUpdate(prevProps: Readonly<IJourneyConfigurationTab>) {
        if (prevProps.loading && !prevProps.journey && this.props.journey) {
            if (!this.unmounting) {
                this.setState(({ domainDefaultSegmentId }) => ({
                    tmpValue: applyJourneyDefaultAudienceSegment(this.props.journey!, domainDefaultSegmentId),
                }))
            }
        }
    }

    public componentWillUnmount() {
        this.unmounting = true
    }

    public render() {
        const { mode } = this.props
        const editableState = this.props.journey
            ? getEditableState(this.props.journey)
            : JourneyEditableState.EDITABLE

        return (
            <div className={getClassNames('cwzd-config')}>
                <ConfigurationEditor
                    mode={mode}
                    domain={this.props.domain}
                    journey={this.journey}
                    editableState={editableState}
                    loading={this.props.loading}
                    onChange={this.handleFormChange}
                    onSubmit={this.handleSubmit}
                    onSave={this.handleSave}
                    onDomainDefaultSegmentResolved={this.handleDomainDefaultSegmentResolved}
                    disableSubmit={this.isSubmitDisabled}
                    disableRouterPrompt={this.props.disableRouterPrompt}
                />
            </div>
        )
    }

    protected getInitialJourneyBuild(domain: DomainDto) {
        const initialChannels = getEnabledDeliveryChannels(domain, true)

        return {
            date_start: dateNow(domain).format('YYYY-MM-DD 00:00'),
            date_time_zone: 'STZ',
            channels: initialChannels,
        }
    }

    protected get journey(): Journey {
        const journey =
            this.state.tmpValue ??
            (this.props.journey ?? (this.getInitialJourneyBuild(this.props.domain) as Journey))

        return applyJourneyAbandonmentDefaults(journey)
    }

    /**
     * The saved journey with the same defaults the edited copy receives, so that opening a journey
     * saved without them does not register as a change.
     */
    protected get savedJourneyWithDefaults(): Journey | undefined {
        if (!this.props.journey) {
            return undefined
        }

        return applyJourneyAbandonmentDefaults(
            applyJourneyDefaultAudienceSegment(this.props.journey, this.state.domainDefaultSegmentId),
        )
    }

    /**
     * Whether the form holds edits the saved journey does not have; this is what enables Save.
     * Status is compared on its own because serializeJourney leaves status out of the payload it
     * diffs, so a status change would otherwise go undetected here.
     */
    protected get hasUnsavedChanges(): boolean {
        const savedJourney = this.savedJourneyWithDefaults
        if (this.props.mode === JourneyBuilderMode.CREATE || !savedJourney) {
            return false
        }

        const editedJourney = this.journey

        return (
            !deepEqual(serializeJourney(editedJourney), serializeJourney(savedJourney)) ||
            getPublishedJourneyStatusChange(savedJourney, editedJourney) !== undefined
        )
    }

    /**
     * Whether the Active/Paused switch holds a choice the saved journey does not have: for a draft,
     * the status it will publish with; for a published journey, its live status. Leaving the tab is
     * guarded on this alone. Other edits are not guarded, because the audience well still fills in
     * the delivery channel when it mounts on a single-channel domain, and guarding that would keep a
     * journey nobody edited on this tab.
     */
    public get hasUnsavedStatusSwitchChange(): boolean {
        const savedJourney = this.props.journey
        if (this.props.mode === JourneyBuilderMode.CREATE || !savedJourney) {
            return false
        }

        const editedJourney = this.journey
        if (isJourneyUnpublished(savedJourney)) {
            return getJourneyStatusOnPublish(editedJourney) !== getJourneyStatusOnPublish(savedJourney)
        }

        return getPublishedJourneyStatusChange(savedJourney, editedJourney) !== undefined
    }

    protected get isSubmitDisabled(): boolean {
        const isLoading = this.props.loading ?? false
        const localJourney = this.journey
        const name = localJourney.name
        const nameIsTooShort = !name || name.length < 3
        const startDateIsMissing = localJourney.date_start === undefined
        const changesDetected =
            this.props.mode === JourneyBuilderMode.CREATE || !this.props.journey || this.hasUnsavedChanges

        return isLoading || nameIsTooShort || startDateIsMissing || !changesDetected
    }

    protected handleFormChange = async (value: Journey) => {
        this.setState({ tmpValue: value })
    }

    protected handleDomainDefaultSegmentResolved = (segment: SegmentDto | undefined) => {
        if (this.unmounting) {
            return
        }

        const domainDefaultSegmentId = segment?.id
        this.setState(({ tmpValue }) => ({
            domainDefaultSegmentId,
            tmpValue: tmpValue && applyJourneyDefaultAudienceSegment(tmpValue, domainDefaultSegmentId),
        }))
    }

    protected validate(journey: Journey, flags: IConfigurationFlags): { ok: boolean; error?: string } {
        const res: { ok: boolean; error?: string } = { ok: true }

        const config = journey.configuration
        const audience = config.audience
        const journeyType = journey.type
        const isTriggeredJourney = journeyType === JourneyType.TRIGGER
        const isRecurringJourney = journeyType === JourneyType.RECURRING

        const trigger = extractJourneyTrigger(journey)
        const triggerConfig = trigger?.configuration
        const triggerType = triggerConfig?.type

        const setNotOk = (reason: string) => {
            res.ok = false
            res.error = reason
        }

        // journey name
        if (!journey.name) {
            setNotOk('Name is required.')
        }

        // run dates
        if (res.ok) {
            if (!journey.date_start) {
                setNotOk('Start date is required.')
            } else if (flags.endDateEnabled && !journey.date_end) {
                setNotOk('End date is required when enabled.')
            }
        }

        // trigger exists and is set
        if (res.ok && (!trigger || !triggerType)) {
            // TODO: there's got to be a better message for this
            setNotOk('Please complete journey configuration.')
        }

        const triggerParams = triggerConfig?.params
        const isSubscribedTrigger = isTriggeredJourney && triggerType === TriggerType.SUBSCRIBED
        const isAbandonedCartTrigger = isTriggeredJourney && triggerType === TriggerType.ABANDONED_CART
        const isAbandonedViewTrigger = isTriggeredJourney && triggerType === TriggerType.ABANDONED_VIEW
        const isEcommAbandonedTrigger = isTriggeredJourney && (isAbandonedCartTrigger || isAbandonedViewTrigger)

        // criteria
        if (res.ok && isTriggeredJourney) {
            if (flags.criteriaEnabled) {
                const criteria = triggerParams?.criteria
                if (!criteria || Object.keys(criteria).length === 0) {
                    setNotOk('Please configure at least one set of Entry Criteria.')
                }
            }
        }

        if (res.ok && isEcommAbandonedTrigger) {
            if (!config.abandonment_seconds) {
                let abandonedItemName = 'cart'
                if (isAbandonedViewTrigger) {
                    abandonedItemName = 'item'
                }

                setNotOk(`Please set a valid abandoned ${abandonedItemName} time frame.`)
            }

            const itemGroups = config.item_group_targeting
            if (flags.itemGroupsEnabled && !itemGroups?.item_group_ids?.length) {
                setNotOk('Please select at least one Item Group to target.')
            }
        }

        // ensure minimum item views is in range
        if (res.ok && isAbandonedViewTrigger) {
            const minimumItemViews = config.abandoned_browse_minimum_item_views
            if (
                minimumItemViews !== undefined &&
                (minimumItemViews < JOURNEY_ABANDONED_BROWSE_MINIMUM_ITEM_VIEWS ||
                    minimumItemViews > JOURNEY_ABANDONED_BROWSE_MAXIMUM_ITEM_VIEWS)
            ) {
                setNotOk('Minimum Times Viewed must be between 1 and 20')
            }
        }

        // segments
        if (res.ok && !isSubscribedTrigger) {
            const segments = audience?.segment_ids
            const xSegments = audience?.excluded_segment_ids
            if (!segments || segments.length === 0) {
                setNotOk('Please select at least one Audience segment.')
            }

            if (res.ok && xSegments && xSegments.length === 0) {
                setNotOk('Please select at least one excluded Audience segment.')
            }
        }

        // recurrence schedule
        if (res.ok && isRecurringJourney) {
            const schedule = config.schedule
            const scheduleType = schedule?.type as string
            const scheduleSuperType = scheduleType?.replace(/_relative|_specific/, '')

            if (!schedule) {
                setNotOk('Recurrence schedule is required.')
            } else if (!scheduleType) {
                setNotOk('Schedule type is required.')
            } else {
                if (scheduleSuperType === 'daily' || scheduleSuperType === 'weekly') {
                    const frequency = schedule.frequency
                    const allowedDays = schedule.allowed_days
                    const isDaily = scheduleSuperType === 'daily'

                    if (frequency === undefined) {
                        setNotOk(
                            isDaily
                                ? 'Daily schedule frequency is required.'
                                : 'Weekly schedule frequency is required.',
                        )
                    } else if (isNaN(frequency)) {
                        setNotOk((isDaily ? 'Daily' : 'Weekly') + ' schedule frequency is not a valid number.')
                    } else if (frequency <= 0) {
                        setNotOk((isDaily ? 'Daily' : 'Weekly') + ' schedule frequency must be greater than 0.')
                    } else if (!allowedDays || allowedDays.length === 0) {
                        setNotOk(
                            isDaily
                                ? 'At least one day selection is required for the current daily schedule.'
                                : 'Day of the week is required for the current weekly schedule.',
                        )
                    }
                } else if (scheduleType === 'monthly_specific') {
                    if (!schedule.specific_day) {
                        setNotOk('Specific day of the month is required for the current schedule.')
                    }
                } else if (scheduleType === 'monthly_relative') {
                    const rType = schedule.relative_type
                    const relativeDay = schedule.relative_day
                    const ordinal = relativeDay?.ordinal
                    const day = relativeDay?.day

                    if (rType === 'relative_day' && (!ordinal || !day)) {
                        setNotOk(
                            'Invalid realtive monthly schedule. Both week number and day of the week are required.',
                        )
                    }
                }

                if (res.ok && !schedule.specific_time) {
                    setNotOk('Schedule delivery time is required.')
                }

                if (res.ok && !schedule.time_zone) {
                    setNotOk('Schedule delivery time zone is required.')
                }
            }
        }

        // entry cap
        const subscriberFcap = config.subscriber_fcap
        if (res.ok && subscriberFcap) {
            const type = subscriberFcap.type
            const frequency = subscriberFcap.criteria?.frequency

            if (type === 'once_per_criteria') {
                if (frequency === undefined) {
                    setNotOk('Subscriber frequency value is required.')
                } else if (isNaN(frequency)) {
                    setNotOk('Subscriber frequency value is not a valid number.')
                } else if (frequency <= 0) {
                    setNotOk('Subscriber frequency value must be greater than 0.')
                }
            }
        }

        // quiet hours
        if (res.ok && flags.quietHoursEnabled) {
            const quietHours = config.quiet_hours
            if (!quietHours || !quietHours.start || !quietHours.end) {
                setNotOk('Quiet hours must have a valid start time and end time.')
            }
        }

        return res
    }

    protected async ensureInitialTriggerOutStep(journey: Journey) {
        const steps = journey.revision?.steps

        const trigger = extractJourneyTrigger(journey)
        if (trigger && !trigger.id) {
            // string ids are used during node creation
            trigger.id = generateUID()
        }

        let exitStep = steps?.find((s) => s.type === StepType.EXIT)
        if (!exitStep && trigger) {
            const placeholderId = generateShortID()
            const exitId = generateShortID()

            const placeholderStep = {
                id: placeholderId,
                type: StepType.PLACEHOLDER,
                configuration: {
                    out_step_id: exitId,
                },
            }

            exitStep = {
                id: exitId,
                type: StepType.EXIT,
                configuration: {
                    type: ExitType.EXIT,
                    params: {
                        exit_state: ExitStatus.COMPLETED,
                    },
                    in_step_id: trigger.id,
                },
            } as any

            // Wire: TRIGGER → PLACEHOLDER → EXIT
            trigger.configuration = {
                ...trigger.configuration,
                out_step_id: placeholderId,
            }

            if (journey.revision) {
                journey.revision.steps = [...(journey.revision.steps ?? []), placeholderStep as any, exitStep as any]
            }

            // save new steps
            await this.setState({ tmpValue: journey })
        }
    }

    protected handleSubmit = async (_: Journey, flags: IConfigurationFlags) => {
        return this.validateAndSave(flags, (journey) => this.props.onSubmit?.(journey))
    }

    protected handleSave = async (_: Journey, flags: IConfigurationFlags) => {
        return this.validateAndSave(flags, (journey) => this.props.onSave?.(journey))
    }

    /**
     * Validates the form and saves it through `save`. A pause or resume of a published journey
     * is confirmed first, because it changes what subscribers receive the moment it is saved.
     */
    protected async validateAndSave(flags: IConfigurationFlags, save: (journey: Journey) => any) {
        const journey = this.journey
        await this.ensureInitialTriggerOutStep(journey)

        const res = this.validate(journey, flags)
        if (!res.ok) {
            simpleNotification('error', res.error)
            return
        }

        const saveAndAdoptResponse = async () => {
            const submitRes = await save(journey)
            if (submitRes?.ok) {
                this.setState({ tmpValue: submitRes.data })
            }
        }

        const publishedStatusChange = getPublishedJourneyStatusChange(this.props.journey, journey)
        if (!publishedStatusChange) {
            return saveAndAdoptResponse()
        }

        return confirmJourneyStatusChange({
            journey,
            targetStatus: publishedStatusChange,
            domain: this.props.domain,
            onConfirm: saveAndAdoptResponse,
        })
    }
}
